import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { formatCurrency } from '@/lib/formatUtils';
import type { MonthlyExpense } from '@/hooks/useDashboardInsights';
import { type TimePreset, TIME_PRESET_LABELS } from '@/lib/timePeriodUtils';

interface MonthlyExpensesCardProps {
  expenses: MonthlyExpense[];
  footerText?: string;
  preset?: TimePreset;
}

function getExpensesSnapshotLabel(preset?: TimePreset): string {
  if (!preset || preset === 'today') return 'Expenses Snapshot — Month to Date';
  const label = TIME_PRESET_LABELS[preset] ?? preset;
  return `Expenses Snapshot — ${label}`;
}

export const MonthlyExpensesCard: React.FC<MonthlyExpensesCardProps> = ({
  expenses,
  footerText = 'System Recognized Top 5 Monthly Recurring. Feeds Safe to Spend.',
  preset,
}) => {
  return (
    <Card className="bg-card">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {getExpensesSnapshotLabel(preset)}
          </span>
        </div>

        {/* Expense List */}
        <div className="space-y-0">
          {expenses.map((expense, index) => (
            <div 
              key={`${expense.name}-${index}`}
              className={`flex items-center justify-between py-3 ${
                index < expenses.length - 1 ? 'border-b border-border' : ''
              }`}
            >
              <span className="font-medium text-foreground">{expense.name}</span>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                {expense.amount != null && (
                  <span className="font-mono tabular-nums">{formatCurrency(expense.amount)}</span>
                )}
                <span>{expense.dueDate}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {footerText && (
          <p className="text-sm text-muted-foreground mt-4">
            {footerText}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
